import { Trash2 } from "lucide-react";
import type { AdvisoryDraftStatus, AdvisoryTljDraft } from "@/lib/cosmos-api";
import { StatusBadge } from "@/components/ui-ext/primitives";
import { cn } from "@/lib/utils";
import { DRAFT_STATUS_LABELS, DRAFT_STATUS_TONES } from "./advisory-status";

export function AdvisoryDraftsList({
  drafts,
  selectedId,
  onSelect,
  onStatusChange,
  onDelete
}: {
  drafts: AdvisoryTljDraft[];
  selectedId?: string;
  onSelect: (draft: AdvisoryTljDraft) => void;
  onStatusChange: (draft: AdvisoryTljDraft, status: AdvisoryDraftStatus) => void;
  onDelete: (draft: AdvisoryTljDraft) => void;
}) {
  if (drafts.length === 0) {
    return (
      <p className="px-1 text-xs text-muted-foreground">
        No drafts yet — generate the first TLJ suggestion.
      </p>
    );
  }
  return (
    <ul className="space-y-1.5">
      {drafts.map((draft) => (
        <li key={draft.id}>
          <div
            className={cn(
              "group cursor-pointer rounded-[10px] border border-soft p-2.5 transition-colors hover:border-[var(--primary)]",
              selectedId === draft.id &&
                "border-[var(--primary)] bg-[var(--info-soft)]/40"
            )}
            onClick={() => onSelect(draft)}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onSelect(draft);
              }
            }}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-sm font-medium text-anchor">
                  {draft.title}
                </div>
                <div className="mt-0.5 flex items-center gap-1.5 text-[11px] text-muted-foreground">
                  {draft.clientName && (
                    <span className="truncate">{draft.clientName}</span>
                  )}
                  <span>
                    {new Date(draft.updatedAt).toLocaleDateString("en-US", {
                      month: "short",
                      day: "numeric"
                    })}
                  </span>
                </div>
              </div>
              <button
                type="button"
                aria-label={`Delete ${draft.title}`}
                className="invisible text-muted-foreground transition-colors hover:text-[var(--danger)] group-hover:visible"
                onClick={(event) => {
                  event.stopPropagation();
                  onDelete(draft);
                }}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="mt-1.5 flex items-center justify-between gap-2">
              <StatusBadge tone={DRAFT_STATUS_TONES[draft.status]} dot>
                {DRAFT_STATUS_LABELS[draft.status]}
              </StatusBadge>
              <select
                aria-label="Change status"
                value={draft.status}
                onClick={(event) => event.stopPropagation()}
                onChange={(event) => {
                  event.stopPropagation();
                  onStatusChange(
                    draft,
                    event.target.value as AdvisoryDraftStatus
                  );
                }}
                className="h-6 rounded-[8px] border border-soft bg-surface px-1 text-[11px] text-muted-foreground outline-none focus:border-[var(--primary)]"
              >
                {Object.entries(DRAFT_STATUS_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}
